/**
 * How many featured members the sidebar can show.
 * The card measures its list area and asks the API for this many profiles.
 * As the pane grows, rows may condense a little so one more member fits
 * before that extra height turns into a gap.
 */

/** Preferred card. Mobile and short result lists use this many rows when they fit. */
export const MIN_FEATURED_SLOTS = 3;

/** A pane shorter than one full row still shows a single member. */
const MIN_VISIBLE_FEATURED_SLOTS = 1;

/**
 * Tall panes fill with more rows instead of stretching three.
 * 18 rows fill a 4K layout viewport; the featured endpoint rejects limits above 20.
 */
export const MAX_FEATURED_SLOTS = 18;

/**
 * Rendered row height: 65px avatar centered in the row, border included.
 * Three rows must fit the mobile list, which is about 223px after the
 * 280px card, its 15px top padding, and the header.
 */
export const FEATURED_ROW_PX = 74;

/** Furthest a row may shrink to make room for one more member. */
export const FEATURED_ROW_MIN_PX = FEATURED_ROW_PX - 10;

/** Avatar size while rows are at FEATURED_ROW_PX or taller. */
export const FEATURED_AVATAR_PX = 65;

export function featuredSlotCount(listHeight: number): number | null {
    if (!Number.isFinite(listHeight) || listHeight <= 0) return null;
    const full = Math.floor(listHeight / FEATURED_ROW_PX);
    const next = full + 1;
    // One more member, as long as every row stays within a small condense.
    if (next <= MAX_FEATURED_SLOTS && listHeight / next >= FEATURED_ROW_MIN_PX) {
        return Math.max(next, MIN_VISIBLE_FEATURED_SLOTS);
    }
    return Math.min(MAX_FEATURED_SLOTS, Math.max(MIN_VISIBLE_FEATURED_SLOTS, full));
}

/**
 * Share the pane when the rows on screen are shorter than their natural stack
 * (they condensed to admit someone) or taller than the cap (no one left to add).
 * A remainder too small to buy another row stays at the natural height.
 */
export function shouldFillFeaturedRows(listHeight: number, rowCount: number): boolean {
    if (!Number.isFinite(listHeight) || listHeight <= 0 || rowCount <= 0) return false;
    const natural = rowCount * FEATURED_ROW_PX;
    if (listHeight < natural) return true;
    const panePastCap = listHeight > MAX_FEATURED_SLOTS * FEATURED_ROW_PX;
    return panePastCap && listHeight > natural;
}

/** Keep the avatar inside a shrinking row. Growing rows stay at the standard size. */
export function featuredAvatarPx(listHeight: number, rowCount: number): number {
    if (rowCount <= 0 || !Number.isFinite(listHeight) || listHeight <= 0) return FEATURED_AVATAR_PX;
    const rowPx = listHeight / rowCount;
    if (rowPx >= FEATURED_ROW_PX) return FEATURED_AVATAR_PX;
    return Math.max(32, Math.min(FEATURED_AVATAR_PX, Math.floor(rowPx - 8)));
}

/** How many rows to paint. Short results pad toward three when they fit; extras are real profiles only. */
export function visibleFeaturedCount(slotCount: number, profileCount: number): number {
    const floor = Math.min(Math.max(slotCount, 0), MIN_FEATURED_SLOTS);
    if (profileCount <= 0) return floor > 0 ? floor : MIN_FEATURED_SLOTS;
    return Math.min(slotCount, Math.max(floor, profileCount));
}

export function mergeFeaturedProfiles<T extends { _id?: string }>(current: T[], incoming: T[]): T[] {
    const seen = new Set(current.map((profile) => profile._id).filter((id): id is string => Boolean(id)));
    const merged = [...current];
    for (const profile of incoming) {
        if (!profile?._id || seen.has(profile._id)) continue;
        seen.add(profile._id);
        merged.push(profile);
    }
    return merged;
}
